import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    styled,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import { useNotification } from '../../context/NotificationContext';
import { paymentService } from '../../services/api/paymentService';
import { verificationService } from '../../services/api/verificationService';

// Initialize Stripe
const stripePromise = (() => {
    const stripeKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
    
    if (!stripeKey || stripeKey.trim() === '') {
        console.warn('⚠️ Stripe public key not found. Please add REACT_APP_STRIPE_PUBLIC_KEY to your environment variables.');
        return null;
    }
    
    try {
        return loadStripe(stripeKey);
    } catch (error) {
        console.error('❌ Failed to initialize Stripe:', error);
        return null;
    }
})();

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const PageContainer = styled(Box)({
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'rgba(0,0,0,0.7)',
    width: '100%',
    margin: 0,
    padding: 0,
    overflow: 'hidden'
});

const HeroSection = styled(Box)({
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundImage: 'url(images/background.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    minHeight: '100vh',
    padding: '80px 0',
    flexDirection: 'column',
    margin: 0,
    left: 0,
    right: 0
});

const StyledCard = styled(Card)(({ theme }) => ({
    background: '#181D23',
    borderRadius: theme.breakpoints.down('md') ? 16 : 20,
    boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
    color: '#fff',
    marginBottom: 0,
    width: '100%',
    [theme.breakpoints.down('md')]: {
        borderRadius: 16,
        marginBottom: 16,
        padding: theme.breakpoints.down('sm') ? '16px' : '20px'
    }
}));

const VerificationButton = styled(Button)(({ theme }) => ({
    width: '100%',
    background: '#2563EB',
    color: '#fff',
    borderRadius: 8,
    padding: theme.breakpoints.down('md') ? '10px 0' : '12px 0',
    fontWeight: 600,
    fontSize: theme.breakpoints.down('md') ? 14 : 16,
    textTransform: 'none',
    '&:hover': {
        background: '#1d4ed8'
    },
    '&:disabled': {
        background: '#6b7280',
        opacity: 0.7
    },
    [theme.breakpoints.down('sm')]: {
        fontSize: 13,
        padding: '8px 0'
    }
}));

const MainContent = styled(Container)(({ theme }) => ({
    width: '100%',
    maxWidth: 800,
    margin: '0 auto',
    marginTop: theme.breakpoints.down('md') ? 50 : 70,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.breakpoints.down('md') ? 24 : 32,
    [theme.breakpoints.down('md')]: {
        marginTop: 50,
        gap: 24,
        padding: '0 16px'
    },
    [theme.breakpoints.down('sm')]: {
        marginTop: 30,
        gap: 16,
        padding: '0 12px'
    }
}));

const VerificationTitle = styled(Typography)(({ theme }) => ({
    fontSize: theme.breakpoints.down('md') ? 24 : 28,
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: theme.breakpoints.down('md') ? 16 : 24,
    color: '#fff',
    [theme.breakpoints.down('sm')]: {
        fontSize: 20,
        marginBottom: 12
    }
}));

const VerificationSubtitle = styled(Typography)(({ theme }) => ({
    fontSize: theme.breakpoints.down('md') ? 14 : 16,
    textAlign: 'center',
    marginBottom: theme.breakpoints.down('md') ? 24 : 32,
    color: '#b0b8c1',
    [theme.breakpoints.down('sm')]: {
        fontSize: 13,
        marginBottom: 20
    }
}));

const UploadSection = styled(Box)(({ theme }) => ({
    background: '#232933',
    borderRadius: theme.breakpoints.down('md') ? 16 : 20,
    padding: theme.breakpoints.down('md') ? '24px 16px' : '32px',
    marginBottom: theme.breakpoints.down('md') ? 20 : 24,
    [theme.breakpoints.down('sm')]: {
        padding: '20px 12px',
        marginBottom: 16
    }
}));

const UploadTitle = styled(Typography)(({ theme }) => ({
    fontSize: theme.breakpoints.down('md') ? 18 : 20,
    fontWeight: 600,
    marginBottom: theme.breakpoints.down('md') ? 16 : 20,
    color: '#fff',
    [theme.breakpoints.down('sm')]: {
        fontSize: 16,
        marginBottom: 12
    }
}));

const UploadArea = styled(Box)(({ theme }) => ({
    border: '2px dashed #4a5568',
    borderRadius: theme.breakpoints.down('md') ? 12 : 16,
    padding: theme.breakpoints.down('md') ? '32px 16px' : '40px 24px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        borderColor: '#2563EB',
        background: 'rgba(37, 99, 235, 0.05)'
    },
    [theme.breakpoints.down('sm')]: {
        padding: '24px 12px'
    }
}));

const UploadIcon = styled(Box)(({ theme }) => ({
    fontSize: theme.breakpoints.down('md') ? 48 : 64,
    color: '#4a5568',
    marginBottom: theme.breakpoints.down('md') ? 12 : 16,
    [theme.breakpoints.down('sm')]: {
        fontSize: 40,
        marginBottom: 8
    }
}));

const UploadText = styled(Typography)(({ theme }) => ({
    fontSize: theme.breakpoints.down('md') ? 14 : 16,
    color: '#b0b8c1',
    marginBottom: theme.breakpoints.down('md') ? 8 : 12,
    [theme.breakpoints.down('sm')]: {
        fontSize: 13,
        marginBottom: 6
    }
}));

const UploadHint = styled(Typography)(({ theme }) => ({
    fontSize: theme.breakpoints.down('md') ? 12 : 14,
    color: '#6b7280',
    [theme.breakpoints.down('sm')]: {
        fontSize: 11
    }
}));

const FilePreview = styled(Box)(({ theme }) => ({
    background: '#181D23',
    borderRadius: theme.breakpoints.down('md') ? 12 : 16,
    padding: theme.breakpoints.down('md') ? '16px 12px' : '20px 16px',
    marginTop: theme.breakpoints.down('md') ? 16 : 20,
    display: 'flex',
    alignItems: 'center',
    gap: theme.breakpoints.down('md') ? 12 : 16,
    [theme.breakpoints.down('sm')]: {
        padding: '12px 8px',
        marginTop: 12,
        gap: 8
    }
}));

const FileInfo = styled(Box)(({ theme }) => ({
    flex: 1,
    [theme.breakpoints.down('sm')]: {
        fontSize: 13
    }
}));

const FileName = styled(Typography)(({ theme }) => ({
    fontSize: theme.breakpoints.down('md') ? 14 : 16,
    fontWeight: 500,
    color: '#fff',
    marginBottom: theme.breakpoints.down('md') ? 2 : 4,
    [theme.breakpoints.down('sm')]: {
        fontSize: 13
    }
}));

const FileSize = styled(Typography)(({ theme }) => ({
    fontSize: theme.breakpoints.down('md') ? 12 : 14,
    color: '#b0b8c1',
    [theme.breakpoints.down('sm')]: {
        fontSize: 11
    }
}));

const RemoveButton = styled(Button)(({ theme }) => ({
    background: '#dc2626',
    color: '#fff',
    borderRadius: 6,
    padding: theme.breakpoints.down('md') ? '4px 8px' : '6px 12px',
    fontSize: theme.breakpoints.down('md') ? 12 : 14,
    textTransform: 'none',
    '&:hover': {
        background: '#b91c1c'
    },
    [theme.breakpoints.down('sm')]: {
        fontSize: 11,
        padding: '3px 6px'
    }
}));

const IdVerificationPage = () => {
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const query = useQuery();
    const hasInitialized = useRef(false);
    
    const [clientSecret, setClientSecret] = useState<string>('');
    const [isVerifying, setIsVerifying] = useState(false);
    const [verificationCompleted, setVerificationCompleted] = useState(false);

    useEffect(() => {
        if (hasInitialized.current) return;
        
        const token = query.get('token');
        if (!token) {
            showNotification('Invalid verification link. Please check your email.', 'error');
            navigate('/');
            return;
        }

        hasInitialized.current = true;

        // Create verification session
        const createVerificationSession = async () => {
            try {
                const data = await paymentService.createVerificationSession('temp');
                setClientSecret(data.clientSecret);
            } catch (error) {
                console.error('Failed to create verification session:', error);
                showNotification('Failed to initialize verification system. Please try again.', 'error');
            }
        };

        createVerificationSession();
    }, [navigate, showNotification]);

    const handleIdentityVerification = async () => {
        if (!stripePromise) {
            showNotification('Verification system not ready. Please try again.', 'error');
            return;
        }

        try {
            setIsVerifying(true);
            showNotification('Starting identity verification...', 'info');

            const stripe = await stripePromise;
            if (!stripe) {
                throw new Error('Stripe not initialized');
            }

            // Start verification flow using the verification session client secret
            await stripe.verifyIdentity(clientSecret);

            // Call backend to complete verification
            const token = query.get('token');
            if (!token) {
                throw new Error('No verification token found');
            }

            const result = await verificationService.verifyIdentity(token);

            // Handle different response structures
            if (result.success || result.status === 'success' || (result.data && result.data.success)) {
                setVerificationCompleted(true);
                showNotification('Identity verification completed successfully! Check your email for your access PIN.', 'success');
            } else {
                const errorMessage = result.error || result.message || result.data?.error || 'Verification failed';
                throw new Error(errorMessage);
            }
        } catch (error: any) {
            console.error('Identity verification failed:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Identity verification failed. Please try again.';
            showNotification(errorMessage, 'error');
        } finally {
            setIsVerifying(false);
        }
    };

    if (verificationCompleted) {
        return (
            <PageContainer>
                <HeroSection>
                    <Header />
                    <MainContent>
                        <StyledCard>
                            <CardContent sx={{ p: isMobile ? 2 : 4, textAlign: 'center' }}>
                                <Typography 
                                    variant="h4" 
                                    sx={{ 
                                        fontWeight: 600, 
                                        mb: 2,
                                        color: '#22c55e'
                                    }}
                                >
                                    ✅ Verification Complete!
                                </Typography>
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        color: '#b0b8c1',
                                        mb: 3
                                    }}
                                >
                                    Your identity has been verified successfully. Check your email for your botel access PIN.
                                </Typography>
                                <Button
                                    variant="contained"
                                    onClick={() => navigate('/client/dashboard')}
                                    sx={{
                                        backgroundColor: '#2563EB',
                                        '&:hover': { backgroundColor: '#1d4ed8' }
                                    }}
                                >
                                    Return to Home
                                </Button>
                            </CardContent>
                        </StyledCard>
                    </MainContent>
                </HeroSection>
                <Footer />
            </PageContainer>
        );
    }

    if (!stripePromise) {
        return (
            <PageContainer>
                <HeroSection>
                    <Header />
                    <MainContent>
                        <Typography variant="h2" sx={{ textAlign: 'center', color: '#fff' }}>
                            Verification System Unavailable
                        </Typography>
                        <Typography sx={{ textAlign: 'center', color: '#b0b8c1', mt: 2 }}>
                            We're experiencing technical difficulties with our verification system. Please try again later.
                        </Typography>
                    </MainContent>
                </HeroSection>
                <Footer />
            </PageContainer>
        );
    }

    if (!clientSecret) {
        return (
            <PageContainer>
                <HeroSection>
                    <Header />
                <MainContent>
                    <Typography variant="h2" sx={{ textAlign: 'center', color: '#fff' }}>
                        Loading Verification System...
                    </Typography>
                </MainContent>
                </HeroSection>
                <Footer />
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <HeroSection>
                <Header />
                <MainContent>
                    <Typography 
                        variant={isMobile ? "h3" : "h2"} 
                        sx={{ 
                            textAlign: 'center', 
                            color: '#fff',
                            mb: isMobile ? 2 : 3
                        }}
                    >
                        Identity Verification
                    </Typography>
                    
                    <StyledCard>
                        <CardContent sx={{ p: isMobile ? 2 : 4 }}>
                            <Typography 
                                variant="h6" 
                                sx={{ 
                                    fontWeight: 600, 
                                    mb: 2,
                                    color: '#fff'
                                }}
                            >
                                Complete Your Verification
                            </Typography>
                            <Typography 
                                variant="body1" 
                                sx={{ 
                                    color: '#b0b8c1',
                                    mb: 3
                                }}
                            >
                                To complete your check-in process, we need to verify your identity. This is a quick and secure process.
                            </Typography>
                            
                            <VerificationButton
                                onClick={handleIdentityVerification}
                                disabled={isVerifying || !clientSecret}
                            >
                                {isVerifying ? 'Verifying...' : 'Start Identity Verification'}
                            </VerificationButton>
                            
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'center', 
                                alignItems: 'center',
                                mt: 3,
                                gap: 2
                            }}>
                                <img src='icons/secure-icon.svg' alt='secure' />
                                <Typography variant='body2' sx={{ color: '#b0b8c1' }}>
                                    Secure Identity Verification
                                </Typography>
                            </Box>
                        </CardContent>
                    </StyledCard>
                </MainContent>
            </HeroSection>
            <Footer />
        </PageContainer>
    );
};

export default IdVerificationPage; 